/**
 * How long ago something happened, in words.
 *
 * Its own module because two things ask the question for different reasons — the
 * freshness dot asks about the board's last poll, the Recent view asks about a ticket's
 * last edit — and they change for different reasons too. What granularity reads well is
 * one decision; what counts as stale is another.
 *
 * React-free, like `viewPrefs.ts` and `freshness.ts`, so the tests need no DOM.
 */

const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

/**
 * `elapsedMs` is clamped rather than trusted: the two timestamps come from different
 * clocks either side of a network round trip — `Date.now()` against a server-stamped
 * `updatedAt` — and a few seconds of skew the wrong way would otherwise read "-1m ago".
 *
 * Days are the coarsest unit. The board is worked daily and the Recent view shows twenty
 * rows, so "34d ago" is both truthful and the end of the useful range; weeks and months
 * would be a second vocabulary bought for rows nobody scrolls to.
 */
export function agoLabel(elapsedMs: number): string {
  const seconds = Math.round(Math.max(0, elapsedMs) / 1000);
  if (seconds < MINUTE) return 'just now';
  if (seconds < HOUR) return `${Math.floor(seconds / MINUTE)}m ago`;
  if (seconds < DAY) return `${Math.floor(seconds / HOUR)}h ago`;
  return `${Math.floor(seconds / DAY)}d ago`;
}
